import { useRef, useEffect } from "react";
import { useIsMutating } from "@tanstack/react-query";

import { toCount } from "common/utils/formatting";
import { pluralized, unitPlural } from "common/utils/strings";
import { Optional } from "common/utils/types";
import { SubscriptionFrequency } from "common/types";

import {
  sendAddToCart,
  sendSelectItem,
  useSendPostHogAddToCart,
  useSendPostHogProductClick,
  useSendPostHogProductListFilterChange,
  ProductListContext,
} from "client/analytics/utils";
import { ProductListFilterSurface, ProductListSource } from "client/analytics/types";
import { useCartItemAddMutation } from "client/carts/hooks";
import { keys as cartKeys } from "client/carts/hooks/constants";
import { CartCode } from "client/carts/types";

import { FilterChangeHandler, FilterDefinition, FilterValue, ProductSearchResult } from "../types";
import {
  filterValueChanges,
  getAddToCartLabel,
  getAddToCartAndSubscribeLabel,
  getAddToCartPricing,
  getSubscriptionPrice,
  getVariantLabel,
} from "../utils";

export const useScrollToItemSelection = (id: number, isActive: boolean) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<{ [id: number]: Optional<HTMLElement> }>({});

  const setRef = (key: number, ref: Optional<HTMLElement>) => {
    optionRefs.current[key] = ref;
  };

  useEffect(() => {
    // Only run when active on the page to help reset selected values to the proper place
    if (!isActive) {
      return;
    }

    const optionElement = optionRefs.current[id];
    const scrollElement = scrollRef.current;

    if (!optionElement || !scrollElement) {
      return;
    }

    const pagePadding = 16; // Same as what's set in the <Body />
    const scrollRect = scrollElement.getBoundingClientRect();
    const optionRect = optionElement.getBoundingClientRect();

    // Determine how much to scroll by based on how far the element is off the screen
    const scrollRight = Math.min(scrollRect.width - pagePadding - optionRect.right, 0) * -1;
    const scrollLeft = Math.max(pagePadding - optionRect.left, 0) * -1;
    const scrollChange = scrollLeft + scrollRight;

    // Scroll to that position
    scrollElement.scrollLeft += scrollChange;
  }, [id, isActive]);

  return {
    scrollRef,
    setRef,
  };
};

// What the desktop and mobile cards both render, derived once so they stay in sync.
export const useProductCardFields = (
  result: ProductSearchResult,
  listSource: ProductListSource,
  {
    collectionSlug,
    categoryId,
    onNavigate,
  }: Pick<ProductListContext, "collectionSlug" | "categoryId"> & { onNavigate?: () => void } = {},
) => {
  const sendPostHogProductClick = useSendPostHogProductClick();

  const productLine = result.product_line;
  const hasPrescriptionLabel = Boolean(result.patient_prescription_label);
  const isFeatured = productLine.is_featured || productLine.is_loyalty_program_featured;

  const defaultItem = result.default_item;
  const packLabel =
    defaultItem.units_count != null && defaultItem.unit_label
      ? toCount(defaultItem.units_count, defaultItem.unit_label, unitPlural(defaultItem.unit_label))
      : (defaultItem.size_label ?? "");
  // U+2011 is a non-breaking hyphen, so a range like "24.1-60lbs" never wraps mid-number.
  const variantLabel = getVariantLabel(result).replace(/(\d)-(\d)/g, "$1\u2011$2");

  const dim = result.item_dimensions;
  const additionalCount = dim ? dim.count - 1 : 0;
  const sizesLabel = dim ? `${additionalCount} more ${pluralized(additionalCount, dim.label)}` : "";
  const flavorsLabel =
    defaultItem.flavor_count != null ? `${defaultItem.flavor_count} flavors` : "";
  const optionsLabel = [sizesLabel, flavorsLabel].filter(Boolean).join(" · ");

  // Compounds show "Compounded" as the first fact chip rather than a separate icon treatment.
  const facts = productLine.is_compound
    ? [{ label: "Compounded", value: "Compounded" }, ...result.card_facts]
    : result.card_facts;

  const handleClick = () => {
    sendSelectItem(result, listSource);
    sendPostHogProductClick({
      source: listSource,
      id: result.id,
      product_line_id: result.product_line.id,
      default_item_id: result.default_item.id,
      ...(collectionSlug ? { collection_slug: collectionSlug } : {}),
      ...(categoryId ? { category_id: categoryId } : {}),
    });
    onNavigate?.();
  };

  return {
    productLine,
    hasPrescriptionLabel,
    isFeatured,
    variantLabel,
    packLabel,
    optionsLabel,
    facts,
    handleClick,
  };
};

export const useProductCardAddToCart = (
  result: ProductSearchResult,
  listSource: ProductListSource,
  { collectionSlug, categoryId }: Pick<ProductListContext, "collectionSlug" | "categoryId"> = {},
) => {
  const sendPostHogAddToCart = useSendPostHogAddToCart();
  const defaultItem = result.default_item;
  const subscriptionPrice = getSubscriptionPrice(defaultItem);

  // The drawer opens on success, so a second add inside the same round trip would open a second
  // drawer.
  const isAnyAddPending = useIsMutating({ mutationKey: cartKeys.mutateCarts() }) > 0;

  const { mutate: addCartItem, isPending } = useCartItemAddMutation({
    shouldOpenCart: true,
    onSuccess: (cartItemId, quantity, body) => {
      const { price, discount } = getAddToCartPricing({
        oneTimePrice: defaultItem.standard_price,
        subscriptionPrice,
        isSubscription: Boolean(body.subscription),
      });
      sendAddToCart({
        // sendAddToCart falls back to product-level fields unless the item is spelled out.
        item: {
          id: defaultItem.id,
          name: defaultItem.name,
          product: result,
          days_supply: defaultItem.days_supply,
          duration_value: defaultItem.duration_value,
          duration_unit: defaultItem.duration_unit,
        },
        quantity,
        price,
        discount,
      });
      sendPostHogAddToCart({
        source: listSource,
        cart_id: typeof body.cart_id === "number" ? body.cart_id : null,
        cart_item_id: cartItemId,
        item_id: body.item,
        product_id: result.id,
        product_line_id: result.product_line.id,
        patient_id: body.patient_id ?? null,
        flavor_selection_id: body.flavor_selection ?? null,
        quantity,
        unit_price: price,
        discount,
        ...(collectionSlug ? { collection_slug: collectionSlug } : {}),
        ...(categoryId ? { category_id: categoryId } : {}),
      });
    },
  });

  const inputs = defaultItem.add_to_cart;
  if (!inputs) {
    return null;
  }
  return {
    label: getAddToCartLabel(inputs),
    addToCartAndSubscribeLabel: getAddToCartAndSubscribeLabel(inputs),
    // The name and size live in the sibling anchor, so the button repeats them for screen readers.
    itemName: [result.product_line.name, getVariantLabel(result)].filter(Boolean).join(", "),
    isAvailable: inputs.is_available,
    isAddingMultipleUnits: inputs.is_available && inputs.quantity > 1,
    isBlocked: isAnyAddPending,
    isPending,
    recommendedFrequency: inputs.recommended_frequency,
    handle: (subscription: SubscriptionFrequency | undefined) => {
      if (isAnyAddPending) {
        return;
      }
      addCartItem({
        item: defaultItem.id,
        quantity: inputs.quantity,
        cart_id: CartCode.ACTIVE,
        flavor_selection: inputs.flavor_selection_id ?? undefined,
        subscription: subscription ?? null,
      });
    },
  };
};

/**
 * A filter change handler per surface, which also sends a `product_list_filter_change` for each
 * value the change selected or cleared. With `changed`, only that filter's values are sent, so
 * one it sets in passing (For My Pet sets species) isn't reported as the shopper's pick.
 */
export const useFilterChangeFrom = <TSearch, TFilterOptions>({
  definitions,
  filters,
  onChange,
  listSource,
  listContext,
}: {
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[];
  filters: TSearch;
  onChange: (next: Partial<TSearch>) => void;
  listSource: ProductListSource;
  listContext?: ProductListContext;
}) => {
  const sendFilterChange = useSendPostHogProductListFilterChange();

  return (surface: ProductListFilterSurface): FilterChangeHandler<TSearch, TFilterOptions> =>
    (next, changed) => {
      filterValueChanges({
        definitions: changed ? [changed] : definitions,
        before: filters,
        after: { ...filters, ...next },
      }).forEach((change) =>
        sendFilterChange({
          ...change,
          source: listSource,
          surface,
          category_id: listContext?.categoryId,
        }),
      );
      onChange(next);
    };
};
